/**
 * IndexedDB layer for FellowLog offline support.
 *
 *  - `cases`     : local mirror of the user's cases (id -> case object)
 *  - `outbox`    : queued write mutations that will be replayed when online
 *  - `meta`      : misc small key/value entries (e.g. last-cases-fetched-at)
 */
import { openDB } from "idb";

const DB_NAME = "fellowlog-offline";
const DB_VERSION = 1;

let _dbPromise = null;

export function getDB() {
  if (!_dbPromise) {
    _dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("cases")) {
          db.createObjectStore("cases", { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains("outbox")) {
          const store = db.createObjectStore("outbox", {
            keyPath: "id",
            autoIncrement: true,
          });
          store.createIndex("by_created", "createdAt");
        }
        if (!db.objectStoreNames.contains("meta")) {
          db.createObjectStore("meta");
        }
      },
    });
  }
  return _dbPromise;
}

/* ------------------------------ cases mirror ------------------------------ */
export async function putCases(cases) {
  const db = await getDB();
  const tx = db.transaction("cases", "readwrite");
  await Promise.all(cases.map((c) => tx.store.put(c)));
  await tx.done;
}

export async function putCase(c) {
  const db = await getDB();
  await db.put("cases", c);
}

export async function getAllCases() {
  const db = await getDB();
  return db.getAll("cases");
}

export async function getCase(id) {
  const db = await getDB();
  return db.get("cases", id);
}

export async function deleteCaseLocal(id) {
  const db = await getDB();
  await db.delete("cases", id);
}

/* --------------------------------- outbox --------------------------------- */
export async function enqueue(mutation) {
  const db = await getDB();
  const record = {
    ...mutation,
    createdAt: new Date().toISOString(),
    status: "pending",
    attempts: 0,
  };
  const id = await db.add("outbox", record);
  return { ...record, id };
}

export async function getPending() {
  const db = await getDB();
  return db.getAllFromIndex("outbox", "by_created");
}

export async function removeOutbox(id) {
  const db = await getDB();
  await db.delete("outbox", id);
}

export async function updateOutbox(id, patch) {
  const db = await getDB();
  const existing = await db.get("outbox", id);
  if (!existing) return null;
  const next = { ...existing, ...patch };
  await db.put("outbox", next);
  return next;
}

export async function countPending() {
  const db = await getDB();
  return db.count("outbox");
}

/* ---------------------------------- meta ---------------------------------- */
export async function setMeta(key, value) {
  const db = await getDB();
  await db.put("meta", value, key);
}

export async function getMeta(key) {
  const db = await getDB();
  return db.get("meta", key);
}
